import { COURT_WIDTH, FULL_LENGTH, HALF_LENGTH } from '@hoopify/shared';

/**
 * Game day whiteboard. Everything is in court metres (x across 0–15, y along
 * the court from the near baseline), so drawings survive the phone rotating
 * and the court switching orientation to fill the screen.
 */

export type CourtKind = 'half' | 'full';
export type InkColour = 'ink' | 'pink' | 'blue' | 'orange';
export type Tool = 'pen' | 'eraser' | 'move';

export interface Pt {
  x: number;
  y: number;
}

export interface Stroke {
  id: string;
  /** Erase strokes rub out what's under them, like a real whiteboard. */
  erase: boolean;
  colour: InkColour;
  /** Line width in metres. */
  width: number;
  points: Pt[];
}

export interface Marker {
  id: string;
  kind: 'offence' | 'defence' | 'ball';
  label: string;
  x: number;
  y: number;
}

export interface Board {
  id: string;
  name: string;
  court: CourtKind;
  strokes: Stroke[];
  markers: Marker[];
  /** Players moved to the sideline while on half court because they were in the far half; back in place on full court. */
  parked?: Record<string, Pt>;
}

export interface GameDay {
  boards: Board[];
  active: string;
  /** Dark court by default; glare and battery friendly. */
  light: boolean;
}

export const MAX_BOARDS = 5;
export const PAD = 0.6;
export const WIDTHS = { thin: 0.14, thick: 0.32 } as const;
export const ERASER_WIDTH = 1.1;
/** Marker radius in metres; drawn between 14 and 24 px. */
export const MARKER_R = 0.5;

export const courtLen = (c: CourtKind) => (c === 'half' ? HALF_LENGTH : FULL_LENGTH);

let n = 0;
export const newId = () => `${Date.now().toString(36)}${(n++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Players start lined up on the sidelines: attackers left, defenders right, ball beside the attackers. */
export function homeMarkers(court: CourtKind): Marker[] {
  const L = courtLen(court);
  const ys = court === 'half' ? [2.5, 4.5, 6.5, 8.5, 10.5] : [8, 10.5, 13, 15.5, 18];
  return [
    ...ys.map((y, i) => ({ id: `o${i + 1}`, kind: 'offence' as const, label: String(i + 1), x: 0.45, y })),
    ...ys.map((y, i) => ({ id: `x${i + 1}`, kind: 'defence' as const, label: String(i + 1), x: COURT_WIDTH - 0.45, y })),
    { id: 'ball', kind: 'ball' as const, label: '', x: 0.45, y: court === 'half' ? Math.min(L - 1, 12.5) : 20.5 },
  ];
}

export function homeOf(court: CourtKind, id: string) {
  return homeMarkers(court).find((m) => m.id === id)!;
}

export function newBoard(name: string, court: CourtKind = 'half'): Board {
  return { id: newId(), name, court, strokes: [], markers: homeMarkers(court) };
}

export function initialGameDay(): GameDay {
  const b = newBoard('Board 1');
  return { boards: [b], active: b.id, light: false };
}

/** True when a point is on the court (with a little room past the lines). */
export function onCourt(p: Pt, court: CourtKind) {
  return p.x >= -PAD && p.x <= COURT_WIDTH + PAD && p.y >= -PAD && p.y <= courtLen(court) + PAD;
}

/** Moves a marker; dropped off the court, it goes back to its place on the sideline. */
export function moveMarker(b: Board, id: string, to: Pt): Board {
  const inside = onCourt(to, b.court);
  const home = homeOf(b.court, id);
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  const L = courtLen(b.court);
  return {
    ...b,
    markers: b.markers.map((m) =>
      m.id === id ? { ...m, x: inside ? clamp(to.x, 0.3, COURT_WIDTH - 0.3) : home.x, y: inside ? clamp(to.y, 0.3, L - 0.3) : home.y } : m,
    ),
  };
}

/** Quick erase: the drawing goes, the players stay where they are. */
export const clearDrawing = (b: Board): Board => ({ ...b, strokes: [] });
/** Clear everything: drawing gone, players back on the sidelines. */
export const clearAll = (b: Board): Board => ({ ...b, strokes: [], markers: homeMarkers(b.court), parked: undefined });

/**
 * Switching full/half keeps everything: a half court is the near half of the full court, in the
 * same metres, so the drawing lines up. On half court, ink in the far half is just out of view,
 * and players there wait on the sideline until it's full court again. Players still on the
 * sideline move to the new court's sideline spots.
 */
export function setCourt(b: Board, court: CourtKind): Board {
  if (b.court === court) return b;
  const parked: Record<string, Pt> = { ...(b.parked ?? {}) };
  const oldHome = homeMarkers(b.court);
  const markers = b.markers.map((m) => {
    const home = homeOf(court, m.id);
    const atHome = oldHome.some((h) => h.id === m.id && h.x === m.x && h.y === m.y);
    if (atHome) {
      const back = court === 'full' ? parked[m.id] : undefined;
      delete parked[m.id];
      return back ? { ...m, ...back } : { ...m, x: home.x, y: home.y };
    }
    delete parked[m.id];
    if (court === 'half' && m.y > courtLen('half') - 0.3) {
      parked[m.id] = { x: m.x, y: m.y };
      return { ...m, x: home.x, y: home.y };
    }
    return m;
  });
  return { ...b, court, markers, parked: Object.keys(parked).length ? parked : undefined };
}

export function isEmpty(b: Board) {
  const home = homeMarkers(b.court);
  return !b.strokes.length && b.markers.every((m) => {
    const h = home.find((x) => x.id === m.id);
    return h && h.x === m.x && h.y === m.y;
  });
}

// ---------- Undo / redo ----------

export interface History {
  past: Board[];
  future: Board[];
}
const LIMIT = 60;

export function record(h: History, before: Board): History {
  return { past: [...h.past, before].slice(-LIMIT), future: [] };
}
export function undo(h: History, current: Board): { h: History; board: Board } | null {
  const prev = h.past[h.past.length - 1];
  if (!prev) return null;
  return { h: { past: h.past.slice(0, -1), future: [current, ...h.future].slice(0, LIMIT) }, board: prev };
}
export function redo(h: History, current: Board): { h: History; board: Board } | null {
  const next = h.future[0];
  if (!next) return null;
  return { h: { past: [...h.past, current].slice(-LIMIT), future: h.future.slice(1) }, board: next };
}

// ---------- Fitting the court to the screen ----------

export interface View {
  /** Pixels per metre. */
  s: number;
  /** Length runs across the screen (full court on a sideways phone). */
  rotated: boolean;
  /** Top-left of the court area (including padding) in px. */
  ox: number;
  oy: number;
  /** Size of the court area in px. */
  w: number;
  h: number;
}

/** Picks the orientation that makes a full court biggest, centred in the box. */
export function fit(court: CourtKind, boxW: number, boxH: number): View {
  const across = COURT_WIDTH + PAD * 2;
  const along = courtLen(court) + PAD * 2;
  const upright = Math.min(boxW / across, boxH / along);
  const sideways = Math.min(boxW / along, boxH / across);
  // Half court always keeps the basket at the top; only the long full court turns.
  const rotated = court === 'full' && sideways > upright * 1.02;
  const s = rotated ? sideways : upright;
  const w = (rotated ? along : across) * s;
  const h = (rotated ? across : along) * s;
  return { s, rotated, ox: (boxW - w) / 2, oy: (boxH - h) / 2, w, h };
}

/**
 * Court metres → screen px. Upright: basket at the top. Sideways: the near
 * basket on the left, so x across the court runs bottom to top.
 */
export function toScreen(v: View, p: Pt): Pt {
  if (!v.rotated) return { x: v.ox + (p.x + PAD) * v.s, y: v.oy + (p.y + PAD) * v.s };
  return { x: v.ox + (p.y + PAD) * v.s, y: v.oy + (COURT_WIDTH + PAD - p.x) * v.s };
}

export function toCourt(v: View, q: Pt): Pt {
  if (!v.rotated) return { x: (q.x - v.ox) / v.s - PAD, y: (q.y - v.oy) / v.s - PAD };
  return { x: COURT_WIDTH + PAD - (q.y - v.oy) / v.s, y: (q.x - v.ox) / v.s - PAD };
}

/** Drops points closer than ~1.5 px apart so long strokes stay small. */
export function addPoint(points: Pt[], p: Pt, minGap: number): Pt[] {
  const last = points[points.length - 1];
  if (last && Math.hypot(last.x - p.x, last.y - p.y) < minGap) return points;
  return [...points, p];
}

// ---------- Saved on the device ----------

const KEY = 'hoopify.gameday.v1';

export function loadGameDay(): GameDay {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const g = JSON.parse(raw) as GameDay;
      if (Array.isArray(g.boards) && g.boards.length && g.boards.every((b) => b && b.id && Array.isArray(b.strokes) && Array.isArray(b.markers))) {
        return { boards: g.boards.slice(0, MAX_BOARDS), active: g.boards.some((b) => b.id === g.active) ? g.active : g.boards[0]!.id, light: !!g.light };
      }
    }
  } catch {
    /* unreadable or blocked: start fresh */
  }
  return initialGameDay();
}

export function saveGameDay(g: GameDay) {
  try {
    localStorage.setItem(KEY, JSON.stringify(g));
  } catch {
    /* full or blocked: keeps working in memory */
  }
}
